export class ApiMapperUtil {

  // =========================================================================
  // UTILS DE BASE
  // =========================================================================

  static getAge(birthDate: Date | string): number {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }

  /** @deprecated Préférer getProfileTargets + isComparisonResultEligible */
  static getCible(age: number, regime: string, situation: string): string {
    const targets = this.getProfileTargets(age, regime, situation);
    if (targets.includes('TNS_Moins50') && targets.length === 1) {
      return 'TNS';
    }
    if (targets.includes('Senior')) {
      return 'Senior';
    }
    return 'Tout le monde';
  }

  /** Âge à la date d'effet (utilisé pour l'éligibilité produits seniors, etc.). */
  static getAgeAtEffect(birthDate: Date | string | null | undefined, effectDate?: Date | string | null): number {
    const birth = this.parseToDate(birthDate);
    const effect = this.parseToDate(effectDate) || new Date();
    if (!birth) {
      return ApiMapperUtil.getAge(new Date());
    }
    let age = effect.getFullYear() - birth.getFullYear();
    const m = effect.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && effect.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }

  private static parseToDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    const s = String(value).trim();
    const fr = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (fr) {
      const d = new Date(parseInt(fr[3], 10), parseInt(fr[2], 10) - 1, parseInt(fr[1], 10));
      return isNaN(d.getTime()) ? null : d;
    }
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const d = new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3], 10));
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Cibles marketing applicables au profil (union : un produit est gardé s'il matche au moins une cible).
   * TNS &lt; 60 ans : surtout TNS ; TNS ≥ 60 : TNS + Senior + offres grand public ; non-TNS : pas les produits TNS exclusifs.
   */
  static getProfileTargets(ageAtEffect: number, regime: string, situation?: string): string[] {
    const targets: string[] = [];
    const r = this.normalizeRegime(regime);
    const sit = (situation || '').toUpperCase();
    const isTnsRegime = r === 'TNS';
    const isTnsProfession = ['TNS', 'INDEPENDANT', 'COMMERCANT', 'ARTISAN', 'LIBERALE', 'PROF LIBE', 'PROF MED', 'GERANT'].some(kw => sit.includes(kw));
    const isTns = isTnsRegime || isTnsProfession;
    
    const isSeniorProfile = ageAtEffect >= 60 || sit.includes('RETRAITE') || sit.includes('RETRAIT');
    const isJeune = ageAtEffect < 30 || sit.includes('ETUDIANT');
    const isFrontalier = sit.includes('FRONTAL');
    const isExpat = sit.includes('EXPATRI') || sit.includes('EXPAT');

    if (isTns) {
      if (ageAtEffect < 50) {
        if (!isTnsRegime && r === 'GENERAL') {
          targets.push('TNS_Moins50_RegimeGeneral');
        } else {
          targets.push('TNS_Moins50');
        }
      }
      if (ageAtEffect >= 60) {
        targets.push('TNS_Plus60');
      }
    }
    if (isSeniorProfile) {
      targets.push('Senior');
      if (isTns) {
        targets.push('Senior_TNS');
      }
    }

    // "Tout le monde" products are available to everyone (including TNS)
    targets.push('Tout le monde');

    // Age-based targets
    if (ageAtEffect >= 35 && ageAtEffect <= 55) {
      targets.push('Actifs_35_55');
    }
    if (ageAtEffect < 50 && !isSeniorProfile) {
      targets.push('Actifs_Moins50');
    }
    if (ageAtEffect < 75) {
      targets.push('Moins_75');
    }
    if (ageAtEffect >= 35 && ageAtEffect <= 65) {
      targets.push('35_65');
    }
    if (ageAtEffect >= 50 && ageAtEffect <= 80) {
      targets.push('50_80');
    }
    if (ageAtEffect < 60 && !isSeniorProfile) {
      targets.push('Actifs');
    }
    if (sit.includes('RETRAITE') || sit.includes('RETRAIT') || ageAtEffect >= 65) {
      targets.push('Retraites');
    }
    targets.push('Particuliers');

    if (isJeune) {
      targets.push('Jeune');
    }
    if (isFrontalier) {
      targets.push('Frontalier');
    }
    if (isExpat) {
      targets.push('Expatrie');
    }

    return [...new Set(targets)];
  }

  /** Produits réservés aux ≥ 60 ans (Santé Protect, gammes « senior », etc.). */
  static isSeniorAgeRestrictedProduct(productLabel: string, insurer?: string): boolean {
    const p = this.normalizeForMatching(productLabel);
    const i = this.normalizeForMatching(insurer || '');
    if (!p) {
      return false;
    }
    if (p.includes('protect') && (i.includes('alptis') || p.includes('alptis'))) {
      return true;
    }
    if (p.includes('senior') || p.includes('serenissime') || p.includes('vitalite senior')
      || p.includes('capital senior') || p.includes('cap sante senior') || p.includes('sakiohospi')) {
      return true;
    }
    if (p.includes('retraite') || p.includes('retrait')) {
      return true;
    }
    if (p.includes('globale+') || p.includes('globale +')) {
      return true;
    }
    return false;
  }

  static getComparisonResultLabel(result: {
    assurance?: string;
    formule?: string;
    insurer?: string;
    contractName?: string;
  }): string {
    return [result.contractName, result.formule, result.assurance].filter((p) => !!p && String(p).trim()).join(' ');
  }

  static isComparisonResultEligible(
    result: {
      assurance?: string;
      formule?: string;
      insurer?: string;
      contractName?: string;
    },
    profile: { ageAtEffect: number; regime: string; situation?: string }
  ): boolean {
    const label = this.getComparisonResultLabel(result);
    const insurer = (result.insurer || result.assurance || '').toString();
    const age = profile.ageAtEffect;
    const isTns = this.normalizeRegime(profile.regime) === 'TNS';
    const situation = profile.situation || '';

    if (age < 60 && this.isSeniorAgeRestrictedProduct(label, insurer)) {
      return false;
    }

    if (isTns && insurer.toLowerCase().includes('april') && label.toLowerCase().includes('simply')) {
      return false;
    }

    const targets = this.getProfileTargets(age, profile.regime, situation);

    if (isTns && age < 60) {
      return targets.some((t) => this.isProductMatchingTarget(label, insurer, t));
    }

    if (!isTns) {
      const matchesSomeTnsTarget =
        this.isProductMatchingTarget(label, insurer, 'TNS_Moins50')
        || this.isProductMatchingTarget(label, insurer, 'TNS_Plus60');
      const matchesNonTnsTarget = targets.some((t) => this.isProductMatchingTarget(label, insurer, t));
      if (matchesSomeTnsTarget && !matchesNonTnsTarget) {
        return false;
      }
    }

    const matchesProfile = targets.some((t) => this.isProductMatchingTarget(label, insurer, t));
    if (matchesProfile) {
      return true;
    }
    // Assureur absent du catalogue cibles : on conserve la ligne (évite de tout masquer)
    return !this.isInsurerInTargetCatalog(insurer);
  }

  static filterComparisonResultsByProfile<T extends {
    assurance?: string;
    formule?: string;
    insurer?: string;
    contractName?: string;
  }>(
    results: T[],
    profile: { ageAtEffect: number; regime: string; situation?: string } | null | undefined
  ): T[] {
    if (!profile || !Array.isArray(results)) {
      return results || [];
    }
    return results.filter((r) => this.isComparisonResultEligible(r, profile));
  }

  static normalizeRegime(regime: string): string {
    if (!regime) return 'GENERAL';
    const r = regime.toUpperCase().trim();
    if (r.includes('TNS') || r.includes('INDEPENDANT') || r.includes('SSI') || r.includes('RSI')
        || r.includes('PROF_LIBE') || r.includes('PROF_MED') || r.includes('LIBERALE')) return 'TNS';
    if (r.includes('ALSACE') || r.includes('MOSELLE') || r === 'AM' || r === 'RL') return 'ALSACE_MOSELLE';
    if (r.includes('AGRICOLE') || r.includes('MSA') || r.includes('AMEXA')) return 'AGRICOLE';
    return 'GENERAL';
  }

  // =========================================================================
  // ALPTIS
  // =========================================================================
  static getAlptisRegime(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'ALSACE_MOSELLE') return 'ALSACE_MOSELLE';
    if (r === 'AGRICOLE') return 'AMEXA'; // ou REGIME_SALARIES_AGRICOLES selon le statut exact
    if (r === 'TNS') return 'SECURITE_SOCIALE_INDEPENDANTS';
    return 'SECURITE_SOCIALE';
  }

  static getAlptisCSP(situation: string, age: number, regime: string): string {
    const s = (situation || '').toUpperCase();
    const r = this.normalizeRegime(regime);
    if (age >= 60 || s.includes('RETRAITE')) return 'RETRAITES';
    if (r === 'AGRICOLE') return 'AGRICULTEURS_EXPLOITANTS';
    if (r === 'TNS') return 'ARTISANS'; // Par défaut, on pourrait aussi mettre COMMERCANTS_ET_ASSIMILES
    if (s.includes('CADRE')) return 'CADRES';
    if (s.includes('SANS EMPLOI')) return 'PERSONNES_SANS_ACTIVITE_PROFESSIONNELLE';
    return 'EMPLOYES_AGENTS_DE_MAITRISE'; // Par défaut
  }

  static getAlptisCadreExercice(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'TNS') return 'INDEPENDANT';
    return 'SALARIE';
  }

  /** CSP conjoint acceptées par l'API Santé Select (tarifer-niveaux-entiers). */
  private static readonly ALPTIS_SANTE_SELECT_CONJOINT_TNS_CSP = new Set([
    'ARTISANS',
    'COMMERCANTS_ET_ASSIMILES',
    'PROFESSIONS_LIBERALES_ET_ASSIMILES',
    'CHEFS_D_ENTREPRISE',
    'AGRICULTEURS_EXPLOITANTS'
  ]);

  static getAlptisSanteSelectConjointCsp(categorie?: string): string {
    const csp = (categorie || '').trim().toUpperCase();
    if (!csp || ApiMapperUtil.ALPTIS_SANTE_SELECT_CONJOINT_TNS_CSP.has(csp)) {
      return 'EMPLOYES_AGENTS_DE_MAITRISE';
    }
    return csp;
  }

  // =========================================================================
  // APICIL
  // =========================================================================
  static getApicilRegime(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'ALSACE_MOSELLE') return 'ALSACEMOSELLE';
    if (r === 'TNS') return 'SSI';
    return 'GENERAL';
  }

  static getApicilSituation(situation: string, age: number): string {
    const s = (situation || '').toUpperCase();
    if (s.includes('RETRAITE') || age >= 65) return 'RETAITE'; // Typo voulue pour APICIL
    if (s.includes('ETUDIANT')) return 'ETUDIANT';
    if (s.includes('CHOMAGE') || s.includes('SANS EMPLOI')) return 'INACTIF';
    if (this.normalizeRegime(situation) === 'TNS') return 'ACTIF';
    return 'SALARIE';
  }

  // =========================================================================
  // APIVIA
  // =========================================================================
  static getApiviaRegime(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'ALSACE_MOSELLE') return 'AM';
    return 'GE';
  }

  static getApiviaRegimeSocial(regime: string, productCode?: string, categorieSocio?: string): string {
    const raw = (regime || '').toUpperCase().trim();
    if (raw.includes('PROF_LIBE') || raw.includes('PROF_MED')) {
      return 'PROF_LIBE';
    }
    const r = this.normalizeRegime(regime);
    if (r === 'TNS') return 'TNS';
    if (r === 'AGRICOLE') return 'MSA_SAL';

    const code = (productCode || '').toUpperCase();
    const proCodes = new Set(['VITAMT3', 'SAKIT3', 'VITAMH3', 'SAKIH3']);
    const m3Codes = new Set(['VITAMM3', 'SAKIM3']);
    const tnsCsp = new Set([
      'ARTISANS',
      'COMMERCANTS_ET_ASSIMILES',
      'PROFESSIONS_LIBERALES_ET_ASSIMILES',
      'CHEFS_D_ENTREPRISE'
    ]);
    const csp = (categorieSocio || '').trim().toUpperCase();
    if ((proCodes.has(code) || m3Codes.has(code)) && tnsCsp.has(csp)) {
      return 'TNS';
    }
    return 'ASS_SOCIAL';
  }

  /** Codes produits Apivia (liste_produit extranet). */
  static getApiviaProductCode(contractName?: string, formule?: string, assurance?: string): string {
    const label = `${contractName || ''} ${formule || ''} ${assurance || ''}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (label.includes('sakifo') || label.includes('saki')) {
      if (label.includes('hospi')) return 'SAKIH3';
      if (/\bpro\b|sakifo3\s*-\s*pro|sakifo\s*pro/.test(label)) return 'SAKIT3';
      return 'SAKIM3';
    }
    if (label.includes('vitamin') || label.includes('vitam')) {
      if (label.includes('hospi')) return 'VITAMH3';
      if (/\bpro\b|vitamin3\s*-\s*pro|vitamin\s*pro|vitamin3\s*pro/.test(label)) return 'VITAMT3';
      return 'VITAMM3';
    }
    return 'VITAMM3';
  }

  // =========================================================================
  // APRIL
  // =========================================================================
  static getAprilMandatoryScheme(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'ALSACE_MOSELLE') return 'AlsaceMoselle';
    if (r === 'TNS') return 'TNS';
    if (r === 'AGRICOLE') return 'MSA';
    return 'SS'; // April API expects 'SS' instead of 'General'
  }

  // =========================================================================
  // NEOLIANE
  // =========================================================================
  static getNeolianeRegime(regime: string, age: number, situation: string): string {
    const r = this.normalizeRegime(regime);
    const s = (situation || '').toUpperCase();
    
    if (s.includes('RETRAITE') || age >= 65) {
      if (r === 'TNS') return '5'; // Retraité TNS
      return '4'; // Retraité salarié
    }
    if (r === 'TNS') return '2';
    if (r === 'AGRICOLE') return '3'; // 12 pour salarié agricole
    if (r === 'ALSACE_MOSELLE') return '8';
    if (s.includes('ETUDIANT')) return '6';
    if (s.includes('SANS EMPLOI')) return '7';
    if (s.includes('FONCTIONNAIRE')) return '9';
    return '1'; // Salarié par défaut
  }

  static getNeolianeCSP(situation: string, age: number, regime: string): string {
    const r = this.normalizeRegime(regime);
    const s = (situation || '').toUpperCase();
    
    if (s.includes('RETRAITE') || age >= 65) {
      if (r === 'TNS') return '26'; // Retraité TNS
      return '20'; // Retraité
    }
    if (r === 'TNS') return '16'; // Régime indépendant
    if (s.includes('ETUDIANT')) return '23';
    if (s.includes('FONCTIONNAIRE')) return '13';
    if (age >= 55) return '21'; // salarié+55 ans
    return '11'; // salarié 59 ans ou -
  }

  // =========================================================================
  // SWISSLIFE
  // =========================================================================
  static getSwisslifeRegime(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'ALSACE_MOSELLE') return 'RL';
    if (r === 'TNS') return 'RSI';
    if (r === 'AGRICOLE') return 'MSA';
    return 'RG';
  }

  static getSwisslifeStatut(situation: string, regime: string, age: number): string {
    const r = this.normalizeRegime(regime);
    const s = (situation || '').toUpperCase();
    if (s.includes('RETRAITE') || age >= 65) return 'RET';
    if (s.includes('ETUDIANT')) return 'ETU';
    if (r === 'TNS') return 'NS';
    if (r === 'AGRICOLE') return 'EAG';
    return 'SAL';
  }

  // =========================================================================
  // UTWIN
  // =========================================================================
  static getUtwinRegime(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'TNS') return 'SSI'; // UTWIN map often needs SSI for TNS
    if (r === 'AGRICOLE') return 'AGRICOLE';
    return 'RG'; // Par défaut RG
  }

  // =========================================================================
  // ZENIOO
  // =========================================================================
  static getZeniooRegime(regime: string): string {
    const r = this.normalizeRegime(regime);
    if (r === 'ALSACE_MOSELLE') return 'alsace_moselle';
    if (r === 'TNS') return 'tns';
    if (r === 'AGRICOLE') return 'salarie_agricole';
    return 'salarie';
  }

  // =========================================================================
  // LOGIQUE DE CIBLE ET PRODUITS
  // =========================================================================

  static readonly targetMappings: Record<string, { [insurer: string]: string[] }> = {
    '35_65': {
        'APRIL': [
            'zen'
        ]
    },
    '50_80': {
        'APRIL': [
            'tranquillite',
            'simply',
            'serenite'
        ]
    },
    'Actifs_Moins50': {
        'ALPTIS': [
            'lumineis'
        ],
        'ASAF': [
            'mcci palette 2 surcomplementaire'
        ],
        'ECA': [
            'mutactif',
            'insur actif'
        ],
        'NEOLIANE': [
            'innov',
            'pulse',
            'energik',
            'plenitude',
            'alto'
        ],
        'SPVIE': [
            'malin'
        ],
        'SWISSLIFE': [
            'swisslife additionnelle'
        ],
        'ZENIOO': [
            'eco (',
            'evolutive optimum (50ans et',
            '50 ans )',
            ') optimun'
        ]
    },
    'Expatrie': {
        'HENNER': [
            'expat duo',
            'henner expat'
        ],
        'SPVIE': [
            'international'
        ]
    },
    'Frontalier': {
        'ALPTIS': [
            'frontaliers suisses'
        ],
        'HENNER': [
            'viva frontalier'
        ]
    },
    'Jeune': {
        'APRIL': [
            'tranquillite',
            'flexi',
            'mix proximite'
        ],
        'ECA': [
            'mutactif',
            'libreco',
            'insuractif',
            'option budget'
        ],
        'HENNER': [
            'henner solutions actifs'
        ],
        'NEOLIANE': [
            'innov',
            'optima',
            'energik',
            'alto',
            'dynamique'
        ],
        'SPVIE': [
            'malin'
        ],
        'UTWIN': [
            'actif'
        ]
    },
    'Moins_75': {
        'ALPTIS': [
            'pack hospi'
        ],
        'ASAF': [
            'mcci palette 2'
        ],
        'UTWIN': [
            'multi'
        ]
    },
    'Particuliers': {
        'SPVIE': [
            'genius',
            'famille',
            'orion',
            'master'
        ],
        'SWISSLIFE': [
            'swisslife particuliers & madelin'
        ]
    },
    'Retraites': {
        'ASAF': [
            'meana seniors2'
        ],
        'GENERALI': [
            'generali senior'
        ],
        'SPVIE': [
            'senior'
        ],
        'SWISSLIFE': [
            'retraite',
            'swisslife retraites sortie de contrat collectif'
        ],
        'ZENIOO': [
            'proximite',
            'evolutive',
            'focus',
            '60ans et',
            'eco',
            'optimum',
            'equilibre'
        ]
    },
    'Senior': {
        'ALPTIS': [
            'lumineis',
            'select',
            'equilibre',
            'protect'
        ],
        'APRIL': [
            'simply',
            'peps',
            'malakoff humanis',
            'tranquilite',
            'optimale',
            'zen',
            'senior gan',
            'vita',
            'flexi',
            'mix serenite'
        ],
        'ASAF': [
            'meana seniors2',
            'osalys attitude'
        ],
        'ECA': [
            'serenissia',
            'capital senior',
            'serenissime',
            'cap senior',
            'capital vitalite senior'
        ],
        'GENERALI': [
            'generali senior'
        ],
        'HENNER': [
            'henner solutions senior'
        ],
        'NEOLIANE': [
            'dynam',
            'hospi',
            'pulse',
            'optima',
            'plenitude',
            'energyk',
            'performance',
            'alto',
            'quietude'
        ],
        'SOLLY AZAR': [
            'senior'
        ],
        'SPVIE': [
            'senior'
        ],
        'SWISSLIFE': [
            'swisslife confort'
        ],
        'UTWIN': [
            'opti',
            'basic senior',
            'mixi',
            'vital'
        ],
        'ZENIOO': [
            'zen evolutive',
            'zen integrale 55',
            'zen proximite',
            'zen focus 55'
        ]
    },
    'Senior_TNS': {
        'APICIL': [
            'serenite'
        ],
        'SWISSLIFE': [
            'dirigeants',
            'swisslife prevoyance entreprises'
        ]
    },
    'TNS_Moins50': {
        'AESIO': [
            'tns pro'
        ],
        'ALPTIS': [
            'pro',
            'select'
        ],
        'APICIL': [
            'serenite',
            'equilibre'
        ],
        'APIVIA': [
            'vitamin 3',
            'vitamin3 pro'
        ],
        'APRIL': [
            'pro',
            'peps',
            'tranquilite',
            'zen',
            'april malakof',
            'pro start april',
            'mix',
            'flexi'
        ],
        'ASAF': [
            'mcci proevidence 2'
        ],
        'ECA': [
            'protnsia',
            'direct',
            'protnsia evolution'
        ],
        'ENTORIA': [
            'tns confort',
            'tns eco pro',
            'tns exellence pro',
            'tns securite pro',
            'tns essentiel pro'
        ],
        'GENERALI': [
            'generali pro'
        ],
        'HARMONIE': [
            'perform tns'
        ],
        'HENNER': [
            'henner solutions independants'
        ],
        'MALAKOFF': [
            'tns',
            'atout'
        ],
        'NEOLIANE': [
            'plenitude',
            'pulse',
            'alto',
            'energik'
        ],
        'SOLLY AZAR': [
            'tns'
        ],
        'SPVIE': [
            'spvie genius malin'
        ],
        'SWISSLIFE': [
            'swisslife additionnelle',
            'swisslife prevoyance entreprises',
            'dirigeants'
        ],
        'UTWIN': [
            'vital',
            'opti',
            'multi',
            'basic'
        ],
        'ZENIOO': [
            'eco',
            'proximite'
        ]
    },
    'TNS_Moins50_RegimeGeneral': {
        'APRIL': [
            'pro',
            'peps',
            'tranquilite',
            'optimale',
            'zen',
            'april malakof',
            'pro start april',
            'mix',
            'flexi',
            '68 ans )',
            'only ('
        ]
    },
    'TNS_Plus60': {
        'ALPTIS': [
            'select',
            'pro',
            'protect',
            'equilibre'
        ],
        'APRIL': [
            'peps',
            'tranquilite',
            'optimale',
            'proximite',
            'zen',
            'malakof',
            'flexi'
        ],
        'ECA': [
            'mutactif',
            'insur actif'
        ],
        'NEOLIANE': [
            'hospi',
            'innov',
            'optima',
            'plenitude',
            'energyk',
            'dyna',
            'performance',
            'alto',
            'quietude'
        ],
        'SWISSLIFE': [
            'dirigeants',
            'swisslife prevoyance entreprises'
        ],
        'ZENIOO': [
            'proximite',
            'evolutive',
            'focus',
            'eco',
            'optimum',
            'equilibre'
        ]
    },
    'Tout le monde': {
        'APICIL': [
            'serenite',
            'equilibre'
        ],
        'APIVIA': [
            'vitamin 3',
            'hospi',
            'sakifo3'
        ],
        'APRIL': [
            'flexi',
            'only non responsable',
            'mix proximite'
        ],
        'ASAF': [
            'osalys essentiel non responsable',
            'osalys attitude',
            'osalys100% 3'
        ],
        'ECA': [
            'libreco',
            'option budget'
        ],
        'HENNER': [
            'henner solutions actifs'
        ],
        'MALAKOFF': [
            'particulier',
            'atout'
        ],
        'NEOLIANE': [
            'innov',
            'optima',
            'energik',
            'alto',
            'dynamique'
        ],
        'SPVIE': [
            'famille',
            'malin',
            'my claire',
            'nova',
            'smart'
        ],
        'SWISSLIFE': [
            'swisslife madelin',
            'swiss ma formule hospitalisation'
        ],
        'UTWIN': [
            'multi',
            'vital'
        ],
        'ZENIOO': [
            'zen eco',
            'zen focus famille',
            'zen integrale famille'
        ]
    }
};

  private static normalizeForMatching(text: string): string {
    if (!text) return '';
    return text.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .trim();
  }

  /** True si l'assureur est référencé dans au moins une cible du catalogue. */
  static isInsurerInTargetCatalog(insurer: string): boolean {
    const normalized = this.normalizeForMatching(insurer);
    if (!normalized) {
      return false;
    }
    return Object.values(this.targetMappings).some((insurerMap) =>
      Object.keys(insurerMap).some((key) => {
        const k = this.normalizeForMatching(key);
        return normalized.includes(k) || k.includes(normalized);
      })
    );
  }

  static isProductMatchingTarget(productName: string, insurer: string, target: string): boolean {
    if (!target) return true;
    
    const insurerMap = this.targetMappings[target];
    if (!insurerMap) return true; // Si cible non définie dans les règles, on accepte

    const normalizedProductInsurer = this.normalizeForMatching(insurer);
    
    // Trouver la clé d'assureur correspondante
    const matchedInsurerKey = Object.keys(insurerMap).find(key => {
      const normalizedKey = this.normalizeForMatching(key);
      return normalizedProductInsurer.includes(normalizedKey) || normalizedKey.includes(normalizedProductInsurer);
    });

    if (matchedInsurerKey) {
      const allowedKeywords = insurerMap[matchedInsurerKey];
      const prodName = this.normalizeForMatching(productName);
      
      // Match si prodName contient TOUS les mots d'un des mots-clés autorisés
      let matchesTarget = allowedKeywords.some(kw => {
        const kwNorm = this.normalizeForMatching(kw);
        return kwNorm.split(' ').filter(Boolean).every(word => prodName.includes(word));
      });

      // Cas spécial pour TNS_Moins50 et Tout le monde (évite faux positif sur senior)
      if ((target === 'TNS_Moins50' || target === 'Tout le monde') && 
          prodName.includes('senior') && 
          !allowedKeywords.some(kw => this.normalizeForMatching(kw).includes('senior'))) {
         matchesTarget = false;
      }

      // Règles manuelles supplémentaires de normalisation Produit:
      // Si Generali, et "sante pro", autoriser explicitement si cible = TNS_Moins50
      if (matchedInsurerKey === 'GENERALI' && target === 'TNS_Moins50' && prodName.includes('sante pro')) {
        matchesTarget = true;
      }
      
      return matchesTarget;
    }
    
    return false; // Assureur non trouvé dans le mapping pour cette cible
  }
}
